import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AccountStore } from '../stores/account.store';
import { QueueStore } from '../stores/queue.store';
import { ThemeService } from './theme.service';
import { ToastService } from './toast.service';
import { DialogService } from './dialog.service';
import { WorkbenchStore } from '../stores/workbench.store';
import { CopilotStore } from '../stores/copilot.store';
import type { CommandItem } from '../models/command.model';
import type { QueueFilter } from '../stores/queue.store';

const RECENT_KEY = 'ur-workbench.commands.recent';

@Injectable({ providedIn: 'root' })
export class CommandService {
  private readonly router = inject(Router);
  private readonly workbench = inject(WorkbenchStore);
  private readonly queue = inject(QueueStore);
  private readonly account = inject(AccountStore);
  private readonly theme = inject(ThemeService);
  private readonly toast = inject(ToastService);
  private readonly dialog = inject(DialogService);
  private readonly copilot = inject(CopilotStore);

  readonly open = signal(false);
  readonly query = signal('');
  readonly activeIndex = signal(0);
  readonly recent = signal<string[]>(this.readRecent());

  readonly commands = signal<CommandItem[]>(this.buildCommands());

  readonly filtered = computed(() => {
    const q = this.query().trim().toLowerCase();
    const all = this.commands();
    if (!q) return all;
    return all.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        (c.description ?? '').toLowerCase().includes(q) ||
        (c.keywords ?? '').toLowerCase().includes(q),
    );
  });

  toggle(): void {
    this.open.update((o) => !o);
    if (this.open()) {
      this.query.set('');
      this.activeIndex.set(0);
    }
  }

  openPalette(): void {
    this.open.set(true);
    this.query.set('');
    this.activeIndex.set(0);
  }

  closePalette(): void {
    this.open.set(false);
  }

  run(command: CommandItem): void {
    this.remember(command.id);
    this.closePalette();
    command.run();
  }

  moveActive(delta: number): void {
    const list = this.filtered();
    const next = (this.activeIndex() + delta + list.length) % list.length;
    this.activeIndex.set(next);
  }

  selectActive(): void {
    const list = this.filtered();
    const item = list[this.activeIndex()];
    if (item) this.run(item);
  }

  clearRecent(): void {
    this.recent.set([]);
  }

  private remember(id: string): void {
    const next = [id, ...this.recent().filter((r) => r !== id)].slice(0, 5);
    this.recent.set(next);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {
      /* noop */
    }
  }

  private readRecent(): string[] {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  }

  private go(route: string[]): void {
    void this.router.navigate(route);
  }

  private buildCommands(): CommandItem[] {
    const openAccount = (accountId: string) => {
      this.account.selectAccount(accountId);
      this.go(['/classic/account', accountId]);
    };

    const classic = (route: string) => {
      this.workbench.setMode('classic');
      this.go([route]);
    };

    const copilotQueue = (filter: QueueFilter) => {
      this.workbench.setMode('copilot');
      this.copilot.setView('triage');
      this.queue.setFilter(filter);
      this.queue.setSearch('');
      this.go(['/workbench']);
    };

    return [
      /* ---- Navigate ---- */
      { id: 'nav-submissions', label: 'Open Submission Management', group: 'Navigate', icon: 'layers', keywords: 'submission queue cases pipeline', run: () => classic('/classic/submissions') },
      { id: 'nav-profile', label: 'Open Customer Risk Profile', group: 'Navigate', icon: 'building', keywords: 'account risk profile', run: () => classic('/classic/profile') },
      { id: 'nav-risk', label: 'Open Risk Assessment Engine', group: 'Navigate', icon: 'gauge', keywords: 'risk score assessment factors', run: () => classic('/classic/risk') },
      { id: 'nav-pricing', label: 'Open Pricing & Rating', group: 'Navigate', icon: 'calculator', keywords: 'pricing rating premium rate', run: () => classic('/classic/pricing') },
      { id: 'nav-referrals', label: 'Open Referral & Approval', group: 'Navigate', icon: 'arrow-up', keywords: 'referral approval chain', run: () => classic('/classic/referrals') },
      { id: 'nav-documents', label: 'Open Document Management', group: 'Navigate', icon: 'document', keywords: 'documents checklist acord', run: () => classic('/classic/documents') },
      { id: 'nav-compliance', label: 'Open Compliance & Audit', group: 'Navigate', icon: 'shield', keywords: 'compliance audit trail regulatory', run: () => classic('/classic/compliance') },
      { id: 'nav-analytics', label: 'Open Analytics & Dashboards', group: 'Navigate', icon: 'chart', keywords: 'analytics dashboard scorecard kpi', run: () => classic('/classic/analytics') },

      /* ---- Accounts ---- */
      { id: 'acct-ferris', label: 'Open Ferris Bros. Construction LLC', description: 'GL / Umbrella · Kessler & Voss', group: 'Accounts', icon: 'building', keywords: 'ferris bros construction', run: () => openAccount('acct-ferris') },
      { id: 'acct-ironclad', label: 'Open Ironclad Metal Works', description: 'WC / GL · Meridian Direct', group: 'Accounts', icon: 'building', keywords: 'ironclad metal works', run: () => openAccount('acct-ironclad') },
      { id: 'acct-copilot', label: 'Open active account in Copilot', description: 'AI risk synthesis workspace', group: 'Accounts', icon: 'sparkles', keywords: 'copilot account risk synthesis', run: () => this.go(['/workbench/account', this.account.selectedAccountId()]) },

      /* ---- Copilot ---- */
      { id: 'mode-copilot', label: 'Switch to AI Copilot', group: 'Copilot', icon: 'sparkles', shortcut: 'Alt+C', keywords: 'copilot ai mode switch', run: () => copilotQueue('all') },
      { id: 'mode-classic', label: 'Switch to Classic Workbench', group: 'Copilot', icon: 'layout-grid', shortcut: 'Alt+L', keywords: 'classic mode switch', run: () => classic('/classic/submissions') },
      { id: 'queue-high', label: 'Show high-priority queue', group: 'Copilot', icon: 'alert-triangle', keywords: 'high priority queue filter', run: () => copilotQueue('high') },
      { id: 'queue-blocked', label: 'Show blocked accounts', group: 'Copilot', icon: 'shield-off', keywords: 'blocked accounts awaiting broker', run: () => copilotQueue('blocked') },
      { id: 'queue-st', label: 'Show straight-through approvals', group: 'Copilot', icon: 'check-circle', keywords: 'straight through approvals quotes', run: () => copilotQueue('straight-through') },

      /* ---- Actions ---- */
      { id: 'act-quotes', label: 'Bulk issue 5 quotes', group: 'Actions', icon: 'file-check', keywords: 'bulk quote issue straight through', run: () => {
          this.copilot.setActionBanner('Issued 5 quotes to brokers');
          this.toast.success('Quotes issued', '5 straight-through renewals were sent to their brokers.');
        } },
      { id: 'act-chasers', label: 'Review & send broker chasers', group: 'Actions', icon: 'mail', keywords: 'chaser broker request documents', run: () => this.toast.info('Chasers drafted', '2 broker chasers are ready for review.') },
      { id: 'act-referral', label: 'Create referral for David Park', group: 'Actions', icon: 'arrow-up', keywords: 'referral authority override manager', run: () => this.dialog.confirm({
          title: 'Create referral',
          message: 'Draft the TIV authority referral and route it to David Park (UW Manager)?',
          confirmLabel: 'Create & route',
          onConfirm: () => this.toast.success('Referral routed', 'Your referral memo was sent to David Park.'),
        }) },
      { id: 'act-risk', label: 'Run risk analysis', group: 'Actions', icon: 'gauge', keywords: 'risk analysis re-run assessment', run: () => this.toast.success('Risk analysis complete', 'The Risk Assessment Engine finished scoring the active account.') },
      { id: 'act-escalate', label: 'Escalate to UW management', group: 'Actions', icon: 'alert-triangle', keywords: 'escalate manager', run: () => this.dialog.confirm({
          title: 'Escalate account',
          message: 'Escalate the active account to UW Management for review?',
          confirmLabel: 'Escalate',
          onConfirm: () => this.toast.warning('Escalated', 'The account was escalated to UW Management.'),
        }) },

      /* ---- Preferences ---- */
      { id: 'theme-cycle', label: 'Cycle theme (light / dark / ai / system)', group: 'Preferences', icon: 'sun-moon', keywords: 'theme dark light ai mode', run: () => this.toast.info('Theme updated', `Switched to ${this.theme.cycle()} theme.`) },
      { id: 'recent-clear', label: 'Clear recent commands', group: 'Preferences', icon: 'trash', keywords: 'clear recent history commands', run: () => {
          this.clearRecent();
          this.toast.info('Cleared', 'Recent commands were cleared.');
        } },
    ];
  }
}
